import type {
  WorkOrderLetterAddress,
  WorkOrderPrintMode,
  WorkOrderPrintPreviewRow,
  WorkOrderPrintRowSelection,
} from "../../../../../../../../../../../../../api/kehrblatt/letters"
import {
  normalizeWorkOrderLetterAddressForRequest,
  normalizeWorkOrderPrintRowForRequest,
} from "../../../../../../../../../../../../../api/kehrblatt/letters"
import type {
  PrintSelectionOutput,
  PrintTableRow,
  PrintWorkflowRowBinding,
  WorkOrderPrintRowState,
} from "../print-types"
import type { WorkOrderRow } from "../../../../../../../../../../../_shared/_logic/work-orders-table-contract"
import { workOrderRowFromReference } from "./_logic/workOrderReference"
import { formatPropertyCustomerRole } from "../../../../../../../../../../../_shared/_logic/customer-roles"

export const defaultBulkPrintMode = ""
export const manualRecipientValue = "__manual_recipient__"
export const objectAddressRecipientValue = "__object_address_recipient__"
export const impersonalRecipientValue = "__impersonal_recipient__"
export type PrintStrategy = "LETTER" | "NOTICE" | "IGNORE"

export const printModeOptions: { name: string; value: WorkOrderPrintMode | "" }[] = [
  { name: "Terminbrief (persönlich)", value: "PERSONAL_LETTER" },
  { name: "Terminbrief (unpersönlich)", value: "IMPERSONAL_LETTER" },
  { name: "Klebezettel", value: "GENERIC_KLEBEZETTEL" },
]

export const printStrategyOptions: { name: string; value: PrintStrategy | "" }[] = [
  { name: "Strategie Terminbrief", value: "LETTER" },
  { name: "Strategie Aushang", value: "NOTICE" },
  { name: "Ignorieren", value: "IGNORE" },
]

const printModeLabelByValue: Record<WorkOrderPrintMode, string> = {
  PERSONAL_LETTER: "Terminbrief (persönlich)",
  IMPERSONAL_LETTER: "Terminbrief (unpersönlich)",
  GENERIC_KLEBEZETTEL: "Klebezettel",
}

const buildingMultiUnitRolePriority = ["MANAGER", "OWNER"]
const buildingSingleUnitRolePriority = ["TENANT", "OWNER", "MANAGER"]
const residentialUnitRolePriority = ["TENANT"]

export const printRowKey = (row: Pick<PrintTableRow, "id" | "work_order_id">) =>
  row.id || row.work_order_id

const textValue = (value: string | null | undefined) => String(value ?? "").trim()

export const childRowsFor = (row: PrintTableRow): PrintTableRow[] =>
  row.isPrintGroup ? (row.children ?? []) : [row]

const cloneLetterAddress = (address: WorkOrderLetterAddress | null | undefined) =>
  address ? { ...address } : address

export const cloneWorkOrderPrintRowState = (
  state: WorkOrderPrintRowState,
): WorkOrderPrintRowState => ({
  ignored: state.ignored,
  included: state.included,
  mode: state.mode,
  recipientValue: state.recipientValue,
  recipientCustomerId: state.recipientCustomerId,
  recipientContactPointId: state.recipientContactPointId,
  recipientSource: state.recipientSource,
  recipientName: state.recipientName,
  recipientAddress: cloneLetterAddress(state.recipientAddress),
  propertyAddress: cloneLetterAddress(state.propertyAddress),
  personalConfigConfirmed: state.personalConfigConfirmed,
  genericKlebezettelLockedByRowId: state.genericKlebezettelLockedByRowId,
  childSelections: state.childSelections
    ? Object.fromEntries(
        Object.entries(state.childSelections).map(([id, childState]) => [
          id,
          cloneWorkOrderPrintRowState(childState),
        ]),
      )
    : undefined,
})

export const cloneWorkOrderPrintRowStateById = (rowState: Record<string, WorkOrderPrintRowState>) =>
  Object.fromEntries(
    Object.entries(rowState).map(([id, state]) => [id, cloneWorkOrderPrintRowState(state)]),
  ) as Record<string, WorkOrderPrintRowState>

export const rowModeOption = (
  row: WorkOrderPrintPreviewRow,
  mode: WorkOrderPrintMode | null | undefined,
) => row.available_modes.find((entry) => entry.mode === mode) ?? null

export const rowModeAllowed = (
  row: WorkOrderPrintPreviewRow,
  mode: WorkOrderPrintMode | null | undefined,
) => {
  const option = rowModeOption(row, mode)

  return Boolean(option && !option.disabled_reason && !row.disabled_reason)
}

const isResidentialUnitRow = (row: Pick<WorkOrderPrintPreviewRow, "property_unit_type">) =>
  String(row.property_unit_type ?? "")
    .trim()
    .toUpperCase() === "RESIDENTIAL"

const aushangDisabledReason = (row: WorkOrderPrintPreviewRow) => {
  if (!isResidentialUnitRow(row)) return "Aushang ist nur für Wohneinheiten verfügbar."

  const option = rowModeOption(row, "GENERIC_KLEBEZETTEL")

  if (!option && row.supports_generic_klebezettel && !row.disabled_reason) return null

  return option?.disabled_reason ?? (option ? null : "Aushang ist für diese Zeile nicht verfügbar.")
}

export const rowSupportsAushang = (row: WorkOrderPrintPreviewRow) =>
  isResidentialUnitRow(row) &&
  (rowModeAllowed(row, "GENERIC_KLEBEZETTEL") ||
    Boolean(row.supports_generic_klebezettel && !row.disabled_reason))

const printModeAllowedForRow = (
  row: WorkOrderPrintPreviewRow,
  mode: WorkOrderPrintMode | "" | null | undefined,
) => {
  if (!mode) return false

  return mode === "GENERIC_KLEBEZETTEL" ? rowSupportsAushang(row) : rowModeAllowed(row, mode)
}

const allowedPrintModeForRow = (
  row: WorkOrderPrintPreviewRow,
  mode: WorkOrderPrintMode | "" | null | undefined,
): WorkOrderPrintMode | null => {
  if (!mode) return null

  return printModeAllowedForRow(row, mode) ? mode : null
}

export const printStrategyForMode = (mode: WorkOrderPrintMode | null | undefined): PrintStrategy =>
  mode === "GENERIC_KLEBEZETTEL" ? "NOTICE" : "LETTER"

export const printStrategyForState = (state: WorkOrderPrintRowState | undefined): PrintStrategy =>
  state?.ignored ? "IGNORE" : printStrategyForMode(state?.mode)

type PrintModeResolutionOptions = {
  inferModeFromName?: boolean
}

export const effectivePrintModeForState = (
  state: Pick<WorkOrderPrintRowState, "mode" | "recipientName"> | undefined,
  _opts: PrintModeResolutionOptions = {},
): WorkOrderPrintMode => {
  return state?.mode ?? "IMPERSONAL_LETTER"
}

const explicitLetterModeForState = (
  state: WorkOrderPrintRowState | undefined,
): WorkOrderPrintMode => {
  if (state?.mode === "PERSONAL_LETTER" || state?.mode === "IMPERSONAL_LETTER") return state.mode

  return state?.recipientValue && state.recipientValue !== impersonalRecipientValue
    ? "PERSONAL_LETTER"
    : "IMPERSONAL_LETTER"
}

export const firstEnabledMode = (row: WorkOrderPrintPreviewRow): WorkOrderPrintMode =>
  row.available_modes.find((entry) => !entry.disabled_reason)?.mode ?? row.default_mode

const normalizedRole = (value: string | null | undefined) => (value ?? "").trim().toUpperCase()

const hasResidentialUnits = (row: WorkOrderPrintPreviewRow) =>
  row.has_residential_units ?? row.is_multi_unit_property ?? false

const defaultRolePriority = (row: WorkOrderPrintPreviewRow) => {
  const unitType = (row.property_unit_type ?? "").trim().toUpperCase()

  if (unitType === "RESIDENTIAL") return residentialUnitRolePriority

  if (unitType === "BUILDING" && hasResidentialUnits(row)) return buildingMultiUnitRolePriority

  return buildingSingleUnitRolePriority
}

const defaultRecipientOption = (row: WorkOrderPrintPreviewRow) => {
  for (const role of defaultRolePriority(row)) {
    const option = row.recipient_options.find((entry) => normalizedRole(entry.role) === role)

    if (option) return option
  }

  return null
}

const defaultModeForRow = (row: WorkOrderPrintPreviewRow): WorkOrderPrintMode => {
  if (row.default_mode === "GENERIC_KLEBEZETTEL" && printModeAllowedForRow(row, row.default_mode)) {
    return "GENERIC_KLEBEZETTEL"
  }

  if (row.default_mode === "PERSONAL_LETTER" && rowModeAllowed(row, "PERSONAL_LETTER")) {
    return "PERSONAL_LETTER"
  }

  if (rowModeAllowed(row, "PERSONAL_LETTER") && defaultRecipientOption(row)) {
    return "PERSONAL_LETTER"
  }

  if (row.default_mode === "IMPERSONAL_LETTER" && rowModeAllowed(row, "IMPERSONAL_LETTER")) {
    return "IMPERSONAL_LETTER"
  }

  if (rowModeAllowed(row, "IMPERSONAL_LETTER")) return "IMPERSONAL_LETTER"

  if (printModeAllowedForRow(row, row.default_mode)) {
    return row.default_mode
  }

  return firstEnabledMode(row)
}

const recipientValue = (row: WorkOrderPrintPreviewRow) => {
  const fallback = defaultRecipientOption(row) ?? row.recipient_options[0] ?? null
  const binding = (row as PrintTableRow).binding
  const recipientCustomerId = binding
    ? (binding.recipientCustomerId ?? null)
    : (row.selected_recipient_customer_id ?? fallback?.customer_id ?? null)
  const recipientContactPointId = binding
    ? (binding.recipientContactPointId ?? null)
    : (row.selected_recipient_contact_point_id ?? fallback?.contact_point_id ?? null)

  return {
    recipientValue:
      binding?.recipientSource === "OBJECT_ADDRESS"
        ? objectAddressRecipientValue
        : recipientCustomerId || recipientContactPointId
          ? `${recipientCustomerId ?? ""}:${recipientContactPointId ?? ""}`
          : "",
    recipientCustomerId,
    recipientContactPointId,
  }
}

const selectedRecipientOption = (row: WorkOrderPrintPreviewRow) => {
  const fallback = defaultRecipientOption(row) ?? row.recipient_options[0] ?? null
  const binding = (row as PrintTableRow).binding
  const selectedCustomerId = binding
    ? (binding.recipientCustomerId ?? null)
    : (row.selected_recipient_customer_id ?? fallback?.customer_id ?? null)
  const selectedContactPointId = binding
    ? (binding.recipientContactPointId ?? null)
    : (row.selected_recipient_contact_point_id ?? fallback?.contact_point_id ?? null)
  const selected = row.recipient_options.find(
    (option) =>
      option.customer_id === selectedCustomerId &&
      option.contact_point_id === selectedContactPointId,
  )

  return binding ? (selected ?? null) : (selected ?? fallback)
}

const emptyAddress = (): WorkOrderLetterAddress => ({
  label: "",
  lines: [],
  street: null,
  house_number: null,
  zip: null,
  city: null,
  country: null,
  address_extra: null,
})

const addressFrom = (address: WorkOrderLetterAddress | null | undefined): WorkOrderLetterAddress =>
  normalizeWorkOrderLetterAddressForRequest(address) ?? emptyAddress()

const compareNatural = (left: string | null | undefined, right: string | null | undefined) =>
  String(left ?? "").localeCompare(String(right ?? ""), "de", {
    numeric: true,
    sensitivity: "base",
  })

const propertyGroupKey = (row: PrintTableRow) => {
  if (row.property_id) return `property:${row.property_id}`

  if (row.property_label) return `property-label:${row.property_label}`

  return `row:${printRowKey(row)}`
}

const printTargetGroupKey = (row: PrintTableRow) => {
  const baseKey = row.print_group_id ?? propertyGroupKey(row)

  return `${baseKey}:${rowSupportsAushang(row) ? "notice" : "letter"}`
}

const printTargetBaseGroupKey = (row: PrintTableRow) => row.print_group_id ?? propertyGroupKey(row)

const propertySortLabel = (row: PrintTableRow) =>
  row.property_label || row.sourceRow?.propertyAddressLines?.join(" ") || row.property_id || ""

const unitSortLabel = (row: PrintTableRow) =>
  row.unit_label || row.property_unit_id || row.work_order_label || ""

export const sortDeliveryPrintRowsByProperty = (rows: PrintTableRow[]) =>
  [...rows].sort((left, right) => {
    const propertyCompare = compareNatural(propertySortLabel(left), propertySortLabel(right))

    if (propertyCompare) return propertyCompare

    const groupCompare = compareNatural(propertyGroupKey(left), propertyGroupKey(right))

    if (groupCompare) return groupCompare

    const unitCompare = compareNatural(unitSortLabel(left), unitSortLabel(right))

    if (unitCompare) return unitCompare

    const appointmentCompare = compareNatural(left.appointment_start_at, right.appointment_start_at)

    if (appointmentCompare) return appointmentCompare

    return compareNatural(
      left.order_number ?? left.work_order_id,
      right.order_number ?? right.work_order_id,
    )
  })

const hasAddressContent = (address: WorkOrderLetterAddress | null | undefined) =>
  Boolean(
    textValue(address?.country) ||
    textValue(address?.zip) ||
    textValue(address?.city) ||
    textValue(address?.street) ||
    textValue(address?.house_number) ||
    textValue(address?.address_extra) ||
    textValue(address?.label) ||
    (address?.lines ?? []).some((line) => textValue(line)),
  )

const hasCompletePostalAddress = (address: WorkOrderLetterAddress | null | undefined) =>
  Boolean(
    textValue(address?.country) &&
    textValue(address?.zip) &&
    textValue(address?.city) &&
    textValue(address?.street) &&
    textValue(address?.house_number),
  )

const addressReferenceLines = (address: WorkOrderLetterAddress | null | undefined) => {
  const lineValues = (address?.lines ?? []).map(textValue).filter(Boolean)

  if (lineValues.length) return lineValues

  const streetLine = [address?.street, address?.house_number]
    .map(textValue)
    .filter(Boolean)
    .join(" ")
  const cityLine = [address?.zip, address?.city].map(textValue).filter(Boolean).join(" ")

  return [streetLine, cityLine, address?.country].map(textValue).filter(Boolean)
}

const addressReferenceLabel = (address: WorkOrderLetterAddress | null | undefined) =>
  textValue(address?.label) || addressReferenceLines(address).join(", ") || null

const manualRecipientState = (
  row: WorkOrderPrintPreviewRow,
  state: Partial<WorkOrderPrintRowState> = {},
): Pick<
  WorkOrderPrintRowState,
  | "recipientValue"
  | "recipientCustomerId"
  | "recipientContactPointId"
  | "recipientSource"
  | "recipientName"
  | "recipientAddress"
> => ({
  recipientValue: manualRecipientValue,
  recipientCustomerId: null,
  recipientContactPointId: null,
  recipientSource: "MANUAL_ADDRESS",
  recipientName: state.recipientName ?? "",
  recipientAddress: addressFrom(
    hasAddressContent(state.recipientAddress) ? state.recipientAddress : row.property_address,
  ),
})

const existingRecipientState = (
  option: NonNullable<ReturnType<typeof selectedRecipientOption>>,
  state: Partial<WorkOrderPrintRowState> = {},
): Pick<
  WorkOrderPrintRowState,
  | "recipientValue"
  | "recipientCustomerId"
  | "recipientContactPointId"
  | "recipientSource"
  | "recipientName"
  | "recipientAddress"
> => ({
  recipientValue: `${option.customer_id ?? ""}:${option.contact_point_id ?? ""}`,
  recipientCustomerId: option.customer_id ?? null,
  recipientContactPointId: option.contact_point_id ?? null,
  recipientSource: "CUSTOMER_CONTACT_ADDRESS",
  recipientName: option.label ?? state.recipientName ?? null,
  recipientAddress: addressFrom(option.postal_address ?? state.recipientAddress ?? null),
})

const objectAddressRecipientState = (
  row: WorkOrderPrintPreviewRow,
): Pick<
  WorkOrderPrintRowState,
  | "recipientValue"
  | "recipientCustomerId"
  | "recipientContactPointId"
  | "recipientSource"
  | "recipientName"
  | "recipientAddress"
> => {
  const binding = (row as PrintTableRow).binding

  return {
    recipientValue: objectAddressRecipientValue,
    recipientCustomerId: binding?.recipientCustomerId ?? null,
    recipientContactPointId: null,
    recipientSource: "OBJECT_ADDRESS",
    recipientName: binding?.recipientName ?? null,
    recipientAddress: addressFrom(row.property_address),
  }
}

const normalizePersonalRecipientState = (
  row: WorkOrderPrintPreviewRow,
  state: WorkOrderPrintRowState,
): WorkOrderPrintRowState => {
  if (state.recipientCustomerId && state.recipientContactPointId) return state

  if (
    state.recipientSource === "OBJECT_ADDRESS" &&
    state.recipientCustomerId &&
    !state.recipientContactPointId
  ) {
    return state
  }

  if (state.recipientValue === manualRecipientValue) return state

  const fallback = defaultRecipientOption(row) ?? row.recipient_options[0] ?? null

  return {
    ...state,
    ...(fallback ? existingRecipientState(fallback, state) : manualRecipientState(row, state)),
  }
}

export const updateAddressField = (
  address: WorkOrderLetterAddress | null | undefined,
  field: keyof WorkOrderLetterAddress,
  value: string,
): WorkOrderLetterAddress => ({
  ...addressFrom(address),
  [field]: field === "address_extra" ? value.trim() || null : value,
})

export const printableDeliveryRows = (
  rows: PrintTableRow[],
  rowState: Record<string, WorkOrderPrintRowState>,
) =>
  visiblePrintRows(rows, rowState).filter((row) => {
    const state = rowState[printRowKey(row)]
    const mode = effectivePrintModeForState(state)
    const allowed =
      mode === "GENERIC_KLEBEZETTEL" ? rowSupportsAushang(row) : rowModeAllowed(row, mode)

    return Boolean(
      !row.disabled && state?.mode && allowed && !rowSelectionValidationMessage(row, state),
    )
  })

export const rowSelectionValidationMessage = (
  row: PrintTableRow,
  state: WorkOrderPrintRowState | undefined,
  opts: PrintModeResolutionOptions = {},
) => {
  if (!state?.included || state.ignored) return null

  if (row.disabled_reason) return row.disabled_reason

  const mode = effectivePrintModeForState(state, opts)

  if (mode === "PERSONAL_LETTER") {
    if (!textValue(state.recipientName)) return "Name fehlt."

    const address = state.recipientAddress

    if (
      !textValue(address?.country) ||
      !textValue(address?.zip) ||
      !textValue(address?.city) ||
      !textValue(address?.street) ||
      !textValue(address?.house_number)
    ) {
      return "Briefadresse unvollständig."
    }
  }

  if (mode === "IMPERSONAL_LETTER") {
    const address = state.propertyAddress

    if (!row.property_id) return "Liegenschaft fehlt."

    if (
      !textValue(address?.country) ||
      !textValue(address?.zip) ||
      !textValue(address?.city) ||
      !textValue(address?.street) ||
      !textValue(address?.house_number)
    ) {
      return "Liegenschaftsadresse unvollständig."
    }
  }

  return null
}

const uniqueCount = (values: Array<string | null | undefined>) =>
  new Set(values.filter((value): value is string => Boolean(value))).size

const groupSubLabel = (children: PrintTableRow[]) => {
  const selectedUnitCount = uniqueCount(children.map((child) => child.property_unit_id))
  const propertyUnitCount = Math.max(
    0,
    ...children.map((child) => Number(child.property_unit_count ?? 0)).filter(Number.isFinite),
  )
  const appointmentCount = children.length
  const unitLabel =
    propertyUnitCount > 1
      ? selectedUnitCount > 0 && selectedUnitCount < propertyUnitCount
        ? `${selectedUnitCount} von ${propertyUnitCount} Einheiten`
        : `${propertyUnitCount} Einheiten`
      : selectedUnitCount > 1
        ? `${selectedUnitCount} Einheiten`
        : "Gebäudeeinheit"
  const appointmentLabel = `${appointmentCount} ${appointmentCount === 1 ? "Termin" : "Termine"}`

  return `${unitLabel} · ${appointmentLabel}`
}

const groupTooltip = (children: PrintTableRow[]) =>
  children
    .map((child) =>
      [
        child.unit_label,
        child.appointment_label,
        child.order_type_internal_label,
        child.order_number ? `#${child.order_number}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
    )
    .filter(Boolean)
    .join("\n")

const groupModeOptions = (
  children: PrintTableRow[],
): WorkOrderPrintPreviewRow["available_modes"] => {
  const hasPersonalRecipient = children.some((child) =>
    Boolean(rowModeAllowed(child, "PERSONAL_LETTER") && child.recipient_options.length),
  )
  const impersonalBlockedReason = children
    .map((child) => rowModeOption(child, "IMPERSONAL_LETTER")?.disabled_reason ?? null)
    .find((reason): reason is string => Boolean(reason))
  const supportsGeneric = children.some(rowSupportsAushang)
  const options: WorkOrderPrintPreviewRow["available_modes"] = [
    {
      mode: "PERSONAL_LETTER",
      label: printModeLabelByValue.PERSONAL_LETTER,
      disabled_reason: hasPersonalRecipient
        ? null
        : "Kein Ansprechpartner mit Briefadresse verfügbar.",
    },
    {
      mode: "IMPERSONAL_LETTER",
      label: printModeLabelByValue.IMPERSONAL_LETTER,
      disabled_reason: impersonalBlockedReason,
    },
  ]

  if (supportsGeneric) {
    options.push({
      mode: "GENERIC_KLEBEZETTEL",
      label: printModeLabelByValue.GENERIC_KLEBEZETTEL,
      disabled_reason: null,
    })
  }

  return options
}

const defaultGroupMode = (children: PrintTableRow[]): WorkOrderPrintMode => {
  const childDefaults = children.map((child) => defaultModeForRow(child))

  if (childDefaults.length && childDefaults.every((mode) => mode === "GENERIC_KLEBEZETTEL")) {
    return "GENERIC_KLEBEZETTEL"
  }

  return childDefaults.some((mode) => mode === "PERSONAL_LETTER")
    ? "PERSONAL_LETTER"
    : "IMPERSONAL_LETTER"
}

export const buildPrintTargetGroups = (rows: PrintTableRow[]): PrintTableRow[] => {
  const baseGroups = new Map<string, PrintTableRow[]>()
  for (const row of rows) {
    const baseGroupId = printTargetBaseGroupKey(row)
    baseGroups.set(baseGroupId, [...(baseGroups.get(baseGroupId) ?? []), row])
  }

  const groups = new Map<string, PrintTableRow[]>()
  for (const row of rows) {
    const baseGroup = baseGroups.get(printTargetBaseGroupKey(row)) ?? [row]
    const splitByStrategy =
      baseGroup.some(rowSupportsAushang) &&
      baseGroup.some((candidate) => !rowSupportsAushang(candidate))
    const groupId = splitByStrategy ? printTargetGroupKey(row) : printTargetBaseGroupKey(row)
    groups.set(groupId, [...(groups.get(groupId) ?? []), row])
  }

  return Array.from(groups.entries()).map(([groupId, children]) => {
    const first = children[0]
    const defaultMode = defaultGroupMode(children)

    return {
      ...first,
      id: groupId,
      print_group_id: groupId,
      isPrintGroup: true,
      children,
      groupLabel: first.property_label || "Keine Liegenschaft",
      groupSubLabel: groupSubLabel(children),
      groupTooltip: groupTooltip(children),
      appointment_label: `${children.length} ${children.length === 1 ? "Termin" : "Termine"}`,
      announcement_label: `${children.length} ${children.length === 1 ? "Brief" : "Briefe"}`,
      default_mode: defaultMode,
      available_modes: groupModeOptions(children),
      recipient_options: children.flatMap((child) => child.recipient_options),
      selected_recipient_customer_id: null,
      selected_recipient_contact_point_id: null,
      binding: undefined,
      sourceRow: undefined,
    }
  })
}

const canUsePersonalLetter = (child: PrintTableRow) => {
  const recipient = recipientValue(child)

  return Boolean(
    rowModeAllowed(child, "PERSONAL_LETTER") &&
    recipient.recipientCustomerId &&
    (recipient.recipientContactPointId || child.binding?.recipientSource === "OBJECT_ADDRESS"),
  )
}

const buildChildSelection = (child: PrintTableRow): WorkOrderPrintRowState => {
  const defaultMode = defaultModeForRow(child)
  const mode =
    defaultMode === "GENERIC_KLEBEZETTEL"
      ? "GENERIC_KLEBEZETTEL"
      : canUsePersonalLetter(child)
        ? defaultMode
        : "IMPERSONAL_LETTER"
  const state: WorkOrderPrintRowState = {
    included: true,
    mode,
    ...recipientValue(child),
    recipientSource: child.binding?.recipientSource ?? undefined,
    recipientName: selectedRecipientOption(child)?.label ?? null,
    recipientAddress: addressFrom(selectedRecipientOption(child)?.postal_address ?? null),
    propertyAddress: addressFrom(child.property_address ?? null),
    personalConfigConfirmed: true,
  }

  return child.binding?.recipientSource === "OBJECT_ADDRESS"
    ? { ...state, ...objectAddressRecipientState(child) }
    : state
}

export const buildChildSelections = (row: PrintTableRow): Record<string, WorkOrderPrintRowState> =>
  Object.fromEntries(
    childRowsFor(row).map((child) => [printRowKey(child), buildChildSelection(child)]),
  )

export const buildPrintRowState = (
  row: WorkOrderPrintPreviewRow,
  preferredMode: WorkOrderPrintMode | "" | null = defaultBulkPrintMode,
): WorkOrderPrintRowState => {
  const mode = allowedPrintModeForRow(row, preferredMode) ?? defaultModeForRow(row)
  const state: WorkOrderPrintRowState = {
    included: true,
    mode,
    ...recipientValue(row),
    recipientSource: (row as PrintTableRow).binding?.recipientSource ?? undefined,
    recipientName: selectedRecipientOption(row)?.label ?? null,
    recipientAddress: addressFrom(selectedRecipientOption(row)?.postal_address ?? null),
    propertyAddress: addressFrom(row.property_address ?? null),
    personalConfigConfirmed: mode !== "PERSONAL_LETTER" ? true : false,
    childSelections: (row as PrintTableRow).isPrintGroup
      ? buildChildSelections(row as PrintTableRow)
      : undefined,
  }

  if ((row as PrintTableRow).binding?.recipientSource === "OBJECT_ADDRESS") {
    return {
      ...state,
      ...objectAddressRecipientState(row),
    }
  }

  return mode === "PERSONAL_LETTER" ? normalizePersonalRecipientState(row, state) : state
}

export const sourceRowsById = (sourceRows: WorkOrderRow[] = []) =>
  Object.fromEntries(sourceRows.map((row) => [row.id, row])) as Record<string, WorkOrderRow>

const previewRowsByWorkOrderId = (rows: WorkOrderPrintPreviewRow[]) => {
  const grouped = new Map<string, WorkOrderPrintPreviewRow[]>()
  for (const row of rows) {
    const list = grouped.get(row.work_order_id) ?? []
    list.push(row)
    grouped.set(row.work_order_id, list)
  }

  return grouped
}

const previewRowForBinding = (
  grouped: Map<string, WorkOrderPrintPreviewRow[]>,
  workOrderId: string,
) => grouped.get(workOrderId)?.[0] ?? null

const bindPreviewRow = (
  row: WorkOrderPrintPreviewRow,
  binding: PrintWorkflowRowBinding | null,
  sourceRowById: Record<string, WorkOrderRow>,
): PrintTableRow => {
  const selectedRecipientCustomerId =
    binding?.recipientCustomerId ?? row.selected_recipient_customer_id ?? null
  const selectedRecipientContactPointId =
    binding?.recipientContactPointId ?? row.selected_recipient_contact_point_id ?? null
  const sourceRow =
    sourceRowById[row.work_order_id] ?? workOrderRowFromReference(row.work_order_reference)
  const defaultMode = allowedPrintModeForRow(row, binding?.defaultMode) ?? row.default_mode

  return {
    ...row,
    id: binding?.id ?? row.work_order_id,
    default_mode: defaultMode,
    selected_recipient_customer_id: selectedRecipientCustomerId,
    selected_recipient_contact_point_id: selectedRecipientContactPointId,
    markable_step:
      binding?.caseId || binding?.contactOrderId || binding?.contactOrderStepId
        ? {
            case_id: binding.caseId ?? row.markable_step?.case_id ?? "",
            contact_order_id: binding.contactOrderId ?? row.markable_step?.contact_order_id ?? "",
            contact_order_step_id:
              binding.contactOrderStepId ?? row.markable_step?.contact_order_step_id ?? "",
            label: row.markable_step?.label ?? row.announcement_label ?? "Terminbrief",
            status: row.markable_step?.status ?? null,
          }
        : row.markable_step,
    sourceRow: sourceRow ?? undefined,
    binding: binding ?? undefined,
    disabled: Boolean(binding?.disabled),
    disabledReason: binding?.disabledReason ?? null,
  }
}

export const toPrintTableRows = (
  source: WorkOrderPrintPreviewRow[] | PrintTableRow[],
  sourceRows: WorkOrderRow[] | Record<string, WorkOrderRow> = [],
  rowBindings: PrintWorkflowRowBinding[] = [],
  opts: { grouping?: "property" | "flat"; allowManualPersonalLetter?: boolean } = {},
): PrintTableRow[] => {
  const sourceRowById = Array.isArray(sourceRows) ? sourceRowsById(sourceRows) : sourceRows
  const normalizeManualPersonalLetter = (row: PrintTableRow): PrintTableRow => {
    if (!opts.allowManualPersonalLetter) return row

    const modes = row.available_modes ?? []
    const personalMode = modes.find((entry) => entry.mode === "PERSONAL_LETTER")

    if (!personalMode) {
      return {
        ...row,
        available_modes: [
          {
            mode: "PERSONAL_LETTER",
            label: printModeLabelByValue.PERSONAL_LETTER,
            disabled_reason: null,
          },
          ...modes,
        ],
      }
    }

    return {
      ...row,
      available_modes: modes.map((entry) =>
        entry.mode === "PERSONAL_LETTER" ? { ...entry, disabled_reason: null } : entry,
      ),
    }
  }

  if (!rowBindings.length) {
    const rows = source.map((row) => {
      const tableRow = row as PrintTableRow

      return normalizeManualPersonalLetter({
        ...tableRow,
        id: tableRow.id ?? row.work_order_id,
        disabled: Boolean(tableRow.disabled),
        disabledReason: tableRow.disabledReason ?? null,
        sourceRow:
          tableRow.sourceRow ??
          sourceRowById[row.work_order_id] ??
          workOrderRowFromReference(row.work_order_reference) ??
          undefined,
      })
    })

    return opts.grouping === "flat" ? rows : buildPrintTargetGroups(rows)
  }

  const grouped = previewRowsByWorkOrderId(source)
  const rows = rowBindings.flatMap((binding) => {
    const previewRow = previewRowForBinding(grouped, binding.workOrderId)

    return previewRow
      ? [normalizeManualPersonalLetter(bindPreviewRow(previewRow, binding, sourceRowById))]
      : []
  })

  return opts.grouping === "flat" ? rows : buildPrintTargetGroups(rows)
}

export const buildPrintRowStateById = (
  rows: PrintTableRow[],
  preferredMode: WorkOrderPrintMode | "" | null = defaultBulkPrintMode,
) =>
  Object.fromEntries(
    rows.map((row) => [
      printRowKey(row),
      buildPrintRowState(
        row,
        allowedPrintModeForRow(row, row.binding?.defaultMode) ?? preferredMode,
      ),
    ]),
  )

export const visiblePrintRows = (
  rows: PrintTableRow[],
  rowState: Record<string, WorkOrderPrintRowState>,
  _sourceRows: WorkOrderRow[] | Record<string, WorkOrderRow> = [],
) => rows.filter((row) => rowState[printRowKey(row)]?.included)

export const printablePrintRows = (
  rows: PrintTableRow[],
  rowState: Record<string, WorkOrderPrintRowState>,
  sourceRows: WorkOrderRow[] | Record<string, WorkOrderRow> = [],
) =>
  visiblePrintRows(rows, rowState, sourceRows).filter((row) => {
    const state = rowState[printRowKey(row)]
    const mode = effectivePrintModeForState(state)

    return (
      !row.disabled &&
      (mode === "GENERIC_KLEBEZETTEL" ? rowSupportsAushang(row) : rowModeAllowed(row, mode)) &&
      (mode !== "PERSONAL_LETTER" || Boolean(state.personalConfigConfirmed))
    )
  })

export const modeOptionsForRow = (row: WorkOrderPrintPreviewRow) =>
  row.available_modes.map((option) => ({
    name: option.disabled_reason
      ? `${printModeLabelByValue[option.mode] ?? option.label} · ${option.disabled_reason}`
      : (printModeLabelByValue[option.mode] ?? option.label),
    value: option.mode,
    selectable: !option.disabled_reason,
  }))

export const strategyOptionsForRow = (row: WorkOrderPrintPreviewRow) => {
  const letterDisabledReason = rowModeAllowed(row, "IMPERSONAL_LETTER")
    ? null
    : (rowModeOption(row, "IMPERSONAL_LETTER")?.disabled_reason ??
      "Terminbrief ist für diese Zeile nicht verfügbar.")
  const noticeDisabledReason = aushangDisabledReason(row)

  return [
    {
      name: letterDisabledReason
        ? `Strategie Terminbrief · ${letterDisabledReason}`
        : "Strategie Terminbrief",
      value: "LETTER" as const,
      selectable: !letterDisabledReason,
    },
    {
      name: noticeDisabledReason
        ? `Strategie Aushang · ${noticeDisabledReason}`
        : "Strategie Aushang",
      value: "NOTICE" as const,
      selectable: !noticeDisabledReason,
    },
    { name: "Ignorieren", value: "IGNORE" as const, selectable: true },
  ]
}

export const strategyToggleOptionsForRow = (row: WorkOrderPrintPreviewRow) =>
  strategyOptionsForRow(row)
    .filter((option) => option.selectable !== false)
    .map((option) => ({
      label:
        option.value === "IGNORE"
          ? "Ignorieren"
          : option.value === "NOTICE"
            ? "Aushang"
            : "Terminbrief",
      value: option.value,
    }))

const recipientOptionName = (
  name: string | null | undefined,
  option?: WorkOrderPrintPreviewRow["recipient_options"][number],
) => {
  const role = textValue(option?.role)
  const roleLabel =
    textValue(option?.role_label) || (role ? formatPropertyCustomerRole(role.toUpperCase()) : "")
  const recipientName = textValue(name) || "Empfänger"

  return roleLabel ? `${roleLabel}: ${recipientName}` : recipientName
}

export const recipientOptionsForRow = (row: WorkOrderPrintPreviewRow) => {
  const binding = (row as PrintTableRow).binding
  const boundRecipient = row.recipient_options.find(
    (option) => option.customer_id === binding?.recipientCustomerId,
  )
  const offersObjectAddress =
    Boolean(binding?.recipientCustomerId) &&
    Boolean(textValue(binding?.recipientName)) &&
    hasCompletePostalAddress(row.property_address)

  return [
    ...row.recipient_options.map((option) => ({
      name: recipientOptionName(option.label, option),
      value: `${option.customer_id}:${option.contact_point_id}`,
    })),
    ...(offersObjectAddress
      ? [
          {
            name: `${recipientOptionName(binding?.recipientName, boundRecipient)} · Objektadresse`,
            value: objectAddressRecipientValue,
          },
        ]
      : []),
  ]
}

export const recipientChoiceOptionsForRow = (
  row: WorkOrderPrintPreviewRow,
  opts: { includeImpersonal?: boolean } = {},
) => [
  ...recipientOptionsForRow(row),
  { name: "Individuell erfassen", value: manualRecipientValue },
  ...(opts.includeImpersonal === false
    ? []
    : [{ name: "Unpersönlich", value: impersonalRecipientValue }]),
]

export const recipientChoiceValueForState = (
  state: Pick<WorkOrderPrintRowState, "mode" | "recipientValue"> | undefined,
) => {
  if (!state) return ""

  if (state.mode === "IMPERSONAL_LETTER") return impersonalRecipientValue

  if (state.mode === "PERSONAL_LETTER") return state.recipientValue || manualRecipientValue

  return ""
}

export const setRecipientValue = (
  rowState: Record<string, WorkOrderPrintRowState>,
  row: PrintTableRow,
  value: string,
): Record<string, WorkOrderPrintRowState> => {
  const rowId = printRowKey(row)

  if (!value || value === impersonalRecipientValue) {
    return {
      ...rowState,
      [rowId]: {
        ...rowState[rowId],
        mode: "IMPERSONAL_LETTER",
        recipientValue: "",
        recipientCustomerId: null,
        recipientContactPointId: null,
        recipientSource: "IMPERSONAL",
        recipientName: "",
        personalConfigConfirmed: true,
      },
    }
  }

  if (value === manualRecipientValue) {
    return {
      ...rowState,
      [rowId]: {
        ...rowState[rowId],
        mode: "PERSONAL_LETTER",
        recipientValue: manualRecipientValue,
        recipientCustomerId: null,
        recipientContactPointId: null,
        recipientSource: "MANUAL_ADDRESS",
        recipientName: "",
        recipientAddress: addressFrom(
          row.property_address ?? rowState[rowId]?.recipientAddress ?? null,
        ),
        personalConfigConfirmed: true,
      },
    }
  }

  if (value === objectAddressRecipientValue) {
    return {
      ...rowState,
      [rowId]: {
        ...rowState[rowId],
        mode: "PERSONAL_LETTER",
        ...objectAddressRecipientState(row),
        personalConfigConfirmed: true,
      },
    }
  }

  const [customerId, contactPointId] = value.split(":")
  const option = row.recipient_options.find(
    (entry) => entry.customer_id === customerId && entry.contact_point_id === contactPointId,
  )

  return {
    ...rowState,
    [rowId]: {
      ...rowState[rowId],
      mode: "PERSONAL_LETTER",
      recipientValue: value,
      recipientCustomerId: customerId || null,
      recipientContactPointId: contactPointId || null,
      recipientSource: "CUSTOMER_CONTACT_ADDRESS",
      recipientName: option?.label ?? rowState[rowId]?.recipientName ?? null,
      recipientAddress: addressFrom(
        option?.postal_address ?? rowState[rowId]?.recipientAddress ?? null,
      ),
      personalConfigConfirmed: true,
    },
  }
}

export const normalizeRowSelection = (
  rowState: Record<string, WorkOrderPrintRowState>,
  row: PrintTableRow,
) => {
  const rowId = printRowKey(row)
  const state = rowState[rowId]

  if (!state || state.mode !== "PERSONAL_LETTER" || row.isPrintGroup) return rowState

  if (state.recipientCustomerId && state.recipientContactPointId) return rowState

  if (
    state.recipientSource === "OBJECT_ADDRESS" &&
    state.recipientCustomerId &&
    !state.recipientContactPointId
  ) {
    return rowState
  }

  if (state.recipientValue === manualRecipientValue) return rowState

  const fallback = defaultRecipientOption(row) ?? row.recipient_options[0]

  if (!fallback) {
    return {
      ...rowState,
      [rowId]: normalizePersonalRecipientState(row, state),
    }
  }

  return {
    ...rowState,
    [rowId]: {
      ...state,
      ...existingRecipientState(fallback, state),
    },
  }
}

export const setRowMode = (
  rowState: Record<string, WorkOrderPrintRowState>,
  row: PrintTableRow,
  mode: WorkOrderPrintMode,
) => {
  const rowId = printRowKey(row)
  const current = rowState[rowId]

  return normalizeRowSelection(
    {
      ...rowState,
      [rowId]: {
        ...current,
        mode,
        personalConfigConfirmed:
          mode === "PERSONAL_LETTER"
            ? Boolean(current?.mode === "PERSONAL_LETTER" && current?.personalConfigConfirmed)
            : true,
        childSelections:
          mode === "PERSONAL_LETTER"
            ? (current?.childSelections ?? buildChildSelections(row))
            : current?.childSelections,
      },
    },
    row,
  )
}

export const setRowStrategy = (
  rowState: Record<string, WorkOrderPrintRowState>,
  row: PrintTableRow,
  strategy: PrintStrategy,
) => {
  const rowId = printRowKey(row)
  const current = rowState[rowId] ?? buildPrintRowState(row)

  if (strategy === "IGNORE") return { ...rowState, [rowId]: { ...current, ignored: true } }

  const restored = { ...rowState, [rowId]: { ...current, ignored: false } }

  if (printStrategyForMode(current.mode) === strategy) return restored

  return setRowMode(
    restored,
    row,
    strategy === "NOTICE" ? "GENERIC_KLEBEZETTEL" : explicitLetterModeForState(current),
  )
}

const resetUnlockedRowState = (
  state: WorkOrderPrintRowState | undefined,
  row: PrintTableRow,
): WorkOrderPrintRowState => ({
  ...buildPrintRowState(row),
  included: state?.included ?? true,
  genericKlebezettelLockedByRowId: null,
})

const samePropertyRows = (rows: PrintTableRow[], row: PrintTableRow) => {
  const groupKey = propertyGroupKey(row)

  return rows.filter(
    (candidate) => propertyGroupKey(candidate) === groupKey && rowSupportsAushang(candidate),
  )
}

export const setRowModeWithPropertyKlebezettelLock = (
  rowState: Record<string, WorkOrderPrintRowState>,
  rows: PrintTableRow[],
  row: PrintTableRow,
  mode: WorkOrderPrintMode,
) => {
  const rowId = printRowKey(row)
  const current = rowState[rowId]
  let next = setRowMode(rowState, row, mode)

  if (mode === "GENERIC_KLEBEZETTEL") {
    for (const sibling of samePropertyRows(rows, row)) {
      const siblingId = printRowKey(sibling)

      if (siblingId === rowId || !next[siblingId]?.included || next[siblingId]?.ignored) continue

      const siblingNext = setRowMode(next, sibling, "GENERIC_KLEBEZETTEL")
      next = {
        ...siblingNext,
        [siblingId]: {
          ...siblingNext[siblingId],
          genericKlebezettelLockedByRowId: rowId,
          personalConfigConfirmed: true,
        },
      }
    }

    return {
      ...next,
      [rowId]: {
        ...next[rowId],
        genericKlebezettelLockedByRowId: null,
        personalConfigConfirmed: true,
      },
    }
  }

  if (current?.mode === "GENERIC_KLEBEZETTEL") {
    for (const sibling of samePropertyRows(rows, row)) {
      const siblingId = printRowKey(sibling)

      if (siblingId === rowId || next[siblingId]?.genericKlebezettelLockedByRowId !== rowId)
        continue

      next = {
        ...next,
        [siblingId]: resetUnlockedRowState(next[siblingId], sibling),
      }
    }
  }

  return {
    ...next,
    [rowId]: {
      ...next[rowId],
      genericKlebezettelLockedByRowId: null,
    },
  }
}

export const setRowStrategyWithPropertyKlebezettelLock = (
  rowState: Record<string, WorkOrderPrintRowState>,
  rows: PrintTableRow[],
  row: PrintTableRow,
  strategy: PrintStrategy,
) => {
  const rowId = printRowKey(row)

  if (strategy === "IGNORE") {
    const next = { ...rowState }
    for (const candidate of rows) {
      const siblingId = printRowKey(candidate)

      if (siblingId !== rowId && next[siblingId]?.genericKlebezettelLockedByRowId === rowId) {
        next[siblingId] = resetUnlockedRowState(next[siblingId], candidate)
      }
    }

    return setRowStrategy(next, row, strategy)
  }

  rowState = { ...rowState, [rowId]: { ...rowState[rowId], ignored: false } }
  const mode =
    strategy === "NOTICE"
      ? "GENERIC_KLEBEZETTEL"
      : explicitLetterModeForState(rowState[printRowKey(row)])

  return setRowModeWithPropertyKlebezettelLock(rowState, rows, row, mode)
}

export const setGroupPersonalConfiguration = (
  rowState: Record<string, WorkOrderPrintRowState>,
  row: PrintTableRow,
  childSelections: Record<string, WorkOrderPrintRowState>,
): Record<string, WorkOrderPrintRowState> => {
  const rowId = printRowKey(row)
  const nextState: WorkOrderPrintRowState = {
    ...rowState[rowId],
    included: rowState[rowId]?.included ?? true,
    mode: "PERSONAL_LETTER",
    recipientValue: rowState[rowId]?.recipientValue ?? "",
    recipientCustomerId: rowState[rowId]?.recipientCustomerId ?? null,
    recipientContactPointId: rowState[rowId]?.recipientContactPointId ?? null,
    recipientSource: rowState[rowId]?.recipientSource,
    recipientName: rowState[rowId]?.recipientName ?? null,
    recipientAddress: rowState[rowId]?.recipientAddress ?? null,
    propertyAddress: rowState[rowId]?.propertyAddress ?? row.property_address ?? null,
    personalConfigConfirmed: true,
    childSelections,
  }

  return {
    ...rowState,
    [rowId]: nextState,
  }
}

export type ApplyPrintModeResult = {
  rowState: Record<string, WorkOrderPrintRowState>
  total: number
  applied: number
  skipped: number
}

export const applyModeForAllWithResult = (
  rowState: Record<string, WorkOrderPrintRowState>,
  rows: PrintTableRow[],
  bulkMode: WorkOrderPrintMode | "",
  opts: {
    enforcePropertyKlebezettelLock?: boolean
    propertyKlebezettelRows?: PrintTableRow[]
    strategyMode?: boolean
  } = {},
) => {
  if (!bulkMode) {
    return { rowState, total: rows.length, applied: 0, skipped: 0 }
  }

  let next = { ...rowState }
  let applied = 0
  let skipped = 0
  for (const row of rows) {
    const rowId = printRowKey(row)

    if (
      row.disabled ||
      !next[rowId]?.included ||
      next[rowId]?.genericKlebezettelLockedByRowId ||
      (opts.strategyMode
        ? bulkMode === "GENERIC_KLEBEZETTEL"
          ? !rowSupportsAushang(row)
          : !rowModeAllowed(row, "IMPERSONAL_LETTER")
        : !printModeAllowedForRow(row, bulkMode))
    ) {
      skipped += 1
      continue
    }

    if (opts.strategyMode) {
      next = opts.enforcePropertyKlebezettelLock
        ? setRowStrategyWithPropertyKlebezettelLock(
            next,
            opts.propertyKlebezettelRows ?? rows,
            row,
            bulkMode === "GENERIC_KLEBEZETTEL" ? "NOTICE" : "LETTER",
          )
        : setRowStrategy(next, row, bulkMode === "GENERIC_KLEBEZETTEL" ? "NOTICE" : "LETTER")
    } else {
      next = opts.enforcePropertyKlebezettelLock
        ? setRowModeWithPropertyKlebezettelLock(
            next,
            opts.propertyKlebezettelRows ?? rows,
            row,
            bulkMode,
          )
        : setRowMode(next, row, bulkMode)
    }

    applied += 1
  }

  return { rowState: next, total: rows.length, applied, skipped }
}

export const applyModeForAll = (
  rowState: Record<string, WorkOrderPrintRowState>,
  rows: PrintTableRow[],
  bulkMode: WorkOrderPrintMode | "",
) => {
  return applyModeForAllWithResult(rowState, rows, bulkMode).rowState
}

export const removePrintRow = (
  rowState: Record<string, WorkOrderPrintRowState>,
  rowId: string,
) => ({
  ...rowState,
  [rowId]: { ...rowState[rowId], included: false },
})

export const selectionForRow = (
  row: PrintTableRow,
  rowState: Record<string, WorkOrderPrintRowState>,
  opts: PrintModeResolutionOptions = {},
): WorkOrderPrintRowSelection => {
  const state = rowState[printRowKey(row)]
  const mode = effectivePrintModeForState(state, opts)

  return normalizeWorkOrderPrintRowForRequest({
    work_order_id: row.work_order_id,
    work_order_reference: row.sourceRow ?? row.work_order_reference ?? null,
    mode,
    recipient_customer_id: mode === "PERSONAL_LETTER" ? state.recipientCustomerId : null,
    recipient_contact_point_id: mode === "PERSONAL_LETTER" ? state.recipientContactPointId : null,
    work_order_label: row.work_order_label ?? row.order_number ?? null,
    order_number: row.order_number ?? null,
    order_type_internal_label: row.order_type_internal_label ?? row.work_order_label ?? null,
    work_order_sub_label: row.work_order_sub_label ?? null,
    property_id: row.property_id ?? null,
    property_unit_id: row.property_unit_id ?? null,
    property_label: row.property_label ?? null,
    property_address_label:
      addressReferenceLabel(row.property_address) ?? row.property_label ?? null,
    property_address_lines: addressReferenceLines(row.property_address),
    unit_label: row.unit_label ?? null,
    appointment_start_at: row.appointment_start_at ?? null,
    appointment_end_at: row.appointment_end_at ?? null,
    appointment_label: row.appointment_label ?? null,
    recipient_label: mode === "PERSONAL_LETTER" ? (state.recipientName ?? null) : null,
    recipient_name: mode === "PERSONAL_LETTER" ? (state.recipientName ?? null) : null,
    recipient_address: mode === "PERSONAL_LETTER" ? (state.recipientAddress ?? null) : null,
    property_address:
      mode === "IMPERSONAL_LETTER" || state.recipientSource === "OBJECT_ADDRESS"
        ? (state.propertyAddress ?? row.property_address ?? null)
        : null,
    case_id: row.binding?.caseId ?? row.markable_step?.case_id ?? null,
    contact_order_id: row.binding?.contactOrderId ?? row.markable_step?.contact_order_id ?? null,
    contact_order_step_id:
      row.binding?.contactOrderStepId ?? row.markable_step?.contact_order_step_id ?? null,
    letter_delivery_batch_item_id: row.binding?.letterDeliveryBatchItemId ?? null,
    action_case_id: row.binding?.actionCaseId ?? null,
  })
}

export const expandedRowsForSelection = (
  row: PrintTableRow,
  rowState: Record<string, WorkOrderPrintRowState>,
  opts: PrintModeResolutionOptions = {},
): WorkOrderPrintRowSelection[] => {
  const state = rowState[printRowKey(row)]

  if (!row.isPrintGroup) return [selectionForRow(row, rowState, opts)]

  const children = childRowsFor(row)
  const mode = effectivePrintModeForState(state, opts)

  if (mode === "GENERIC_KLEBEZETTEL") {
    const firstChild = children[0]

    return firstChild
      ? [
          selectionForRow(firstChild, {
            [printRowKey(firstChild)]: {
              included: true,
              mode: "GENERIC_KLEBEZETTEL",
              recipientValue: "",
              recipientCustomerId: null,
              recipientContactPointId: null,
              recipientName: null,
              recipientAddress: null,
              propertyAddress: firstChild.property_address ?? null,
              personalConfigConfirmed: true,
            },
          }),
        ]
      : []
  }

  if (mode === "IMPERSONAL_LETTER") {
    return children.map((child) =>
      selectionForRow(child, {
        [printRowKey(child)]: {
          included: true,
          mode: "IMPERSONAL_LETTER",
          recipientValue: "",
          recipientCustomerId: null,
          recipientContactPointId: null,
          recipientName: null,
          recipientAddress: null,
          propertyAddress: child.property_address ?? null,
          personalConfigConfirmed: true,
        },
      }),
    )
  }

  const childSelections = state.childSelections ?? buildChildSelections(row)

  return children
    .map((child) => {
      const childState = childSelections[printRowKey(child)]

      if (!childState?.included) return null

      return selectionForRow(child, { [printRowKey(child)]: childState }, opts)
    })
    .filter((selection): selection is WorkOrderPrintRowSelection => Boolean(selection))
}

export const selectionForRows = (
  rows: PrintTableRow[],
  rowState: Record<string, WorkOrderPrintRowState>,
  opts: PrintModeResolutionOptions = {},
) => rows.flatMap((row) => expandedRowsForSelection(row, rowState, opts))

export const deliveryItemIdForSelection = (row: WorkOrderPrintRowSelection, index: number) =>
  row.letter_delivery_batch_item_id ||
  row.delivery_item_id ||
  row.contact_order_step_id ||
  [
    "delivery",
    row.work_order_id,
    row.mode,
    row.recipient_customer_id ?? "",
    row.recipient_contact_point_id ?? "",
    index,
  ].join(":")

const uniqueDeliveryItemIds = (values: Array<string | null | undefined>) => {
  const seen = new Set<string>()
  const result: string[] = []
  for (const value of values) {
    const text = String(value ?? "").trim()

    if (!text || seen.has(text)) continue

    seen.add(text)
    result.push(text)
  }

  return result
}

export const deliveryItemIdAliasesForSelection = (row: WorkOrderPrintRowSelection, index: number) =>
  uniqueDeliveryItemIds([
    deliveryItemIdForSelection(row, index),
    row.letter_delivery_batch_item_id,
    row.delivery_item_id,
    row.contact_order_step_id,
  ])

export const withDeliveryItemIds = (rows: WorkOrderPrintRowSelection[]) =>
  rows.map((row, index) =>
    normalizeWorkOrderPrintRowForRequest({
      ...row,
      delivery_item_id: deliveryItemIdForSelection(row, index),
    }),
  )

export const mergePreparedRowsForDeliveryDisplay = (
  executableRows: WorkOrderPrintRowSelection[],
  displayRows: WorkOrderPrintRowSelection[],
) => {
  if (!executableRows.length) return displayRows

  const rows: WorkOrderPrintRowSelection[] = []
  const rowByAlias = new Map<string, WorkOrderPrintRowSelection>()

  const addRow = (row: WorkOrderPrintRowSelection, index: number) => {
    const normalizedRow = normalizeWorkOrderPrintRowForRequest(row)
    const aliases = deliveryItemIdAliasesForSelection(normalizedRow, index)
    const existing = aliases.map((alias) => rowByAlias.get(alias)).find(Boolean)

    if (existing) {
      for (const alias of aliases) rowByAlias.set(alias, existing)

      return
    }

    rows.push(normalizedRow)
    for (const alias of aliases) rowByAlias.set(alias, normalizedRow)
  }

  executableRows.forEach(addRow)
  displayRows.forEach(addRow)

  return rows
}
